import type { BookingGap } from "@/components/tpr/BookingFlows/RepresentativeBookingFlow/bookingValidation.utils";
import type { BookingInterceptionResult } from "@/interceptors/booking";
import type { Booking } from "@/types/Booking";
import type { Member } from "@/types/Member";
import type { RecurrencePatternOption } from "@/types/Recurrence";
import type { RecurringConflictDate } from "@/types/CanBookValidation";

export type ScreenId = "book" | "repeat-setup" | "confirmation";

export type BookingType = "slot" | "adhoc";

export interface FormData {
  // From interceptor
  date: Date;
  time: string; // "HHmm" format
  roomId: string;
  roomName: string;
  locationId: string;
  locationSlug: string;
  locationName: string;

  // User selections
  memberId: string;
  memberName: string;
  memberLocked: boolean;
  durationMinutes: number; // Default 30, range 30-1440
  isRecurring: boolean;
  isGroupBooking: boolean; // Tag for manual billing of group sessions; no behaviour gated on it
  bookingType: BookingType; // Elevated users only
  recurrencePattern: RecurrencePatternOption | null;
  // R24 pull-forward — elevated user's chosen earlier slot start (set via "Start Slot Here").
  slotStartOverride?: Date;
  // Elevated past-date waive-billing flag. Default false; elevated users only.
  waiveBilling: boolean;
  // Staff on-behalf booking note. Mandatory when an elevated user books for another member; saved
  // as a booking comment (staff-visible), NOT the member's encrypted private notes. Empty for
  // self-bookings. Ported from the RepresentativeBookingFlow so the calendar / "New booking" flow
  // (which uses this dialog) captures the note too.
  staffNote: string;
}

export interface MakeABookingDialogState {
  // Navigation
  currentScreen: ScreenId;
  screenHistory: ScreenId[];
  navigateTo: (screen: ScreenId) => void;
  goBack: () => void;

  // Form data
  formData: FormData;
  updateFormData: (partial: Partial<FormData>) => void;

  // API state
  isSubmitting: boolean;
  error: string | null;
  createdBooking: Booking | null;

  // Availability state
  isCheckingAvailability: boolean;
  isTimeSlotAvailable: boolean;
  availabilityError: string | null;

  // Per-occurrence conflicts for a recurring booking (from /can-book), each clickable to a card.
  recurringConflictDates: RecurringConflictDate[];

  // Gap warnings (unbookable cross-member gap + continuous-booking same-member gap)
  bookingGaps: BookingGap[];

  // Non-blocking warnings from the /can-book pre-flight (e.g. the termination-waitlist notice when
  // booking for a member whose membership is ending). Displayed but do not block submission.
  serverWarnings: string[];

  // R22 — the member's own overlapping bookings in OTHER rooms on this date. Non-blocking; drives
  // the "you're booking two rooms at once, you'll be charged for both" warning.
  multiRoomConflicts: { startTime: string; endTime: string }[];

  // R1/R2a/R7 — the TimeSlotClickInterceptor verdict. When the interceptor declined the
  // slot click (past date, beyond role range, hard overlap, shuttered room), the dialog must
  // refuse: it shows declineMessage and never renders the booking form / CTA.
  isDeclined: boolean;
  declineMessage: string | null;
  declineReason: string | null;

  // R2b — elevated user booking a past date within the billing window.
  // Computed independently via isPastDateBooking(isElevated, date, today) — NOT derived from the
  // interceptor's warnings array.
  isPastDateWarning: boolean;
  // Count of past occurrences driven by countPastOccurrences — used in the banner copy.
  pastOccurrenceCount: number;

  // User context
  currentUser: Member | null;
  isElevatedUser: boolean;
  // True when an elevated user is booking on behalf of ANOTHER member — a booking note is then
  // mandatory (drives the note field + submit gate). False for self-bookings.
  requiresStaffNote: boolean;

  // Actions
  // `overrideBufferConflict` is only ever passed true by confirmBufferOverride below — normal
  // callers invoke submitBooking() with no argument.
  submitBooking: (overrideBufferConflict?: boolean) => Promise<void>;
  resetFlow: () => void;
  onClose?: () => void;

  // Task 10 — elevated cross-member buffer override. Opens when the REAL create attempt 409s with
  // conflictType "buffer_overlap" for an elevated caller (never for the /can-book preflight, which
  // doesn't surface an overridable conflict). Non-elevated users never see this — they keep the
  // existing hard `error` above.
  bufferOverrideOpen: boolean;
  bufferOverrideMessage: string | null;
  /** "Book anyway" — resubmits the identical payload plus overrideBufferConflict: true. */
  confirmBufferOverride: () => Promise<void>;
  setBufferOverrideOpen: (open: boolean) => void;
}

export interface MakeABookingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingResult: BookingInterceptionResult | null;
  onBookingCreated?: (booking: Booking) => void;
}

export interface MakeABookingDialogProviderProps {
  bookingResult: BookingInterceptionResult | null;
  onClose?: () => void;
  onBookingCreated?: (booking: Booking) => void;
  children: React.ReactNode;
}

export const DEFAULT_DURATION_MINUTES = 60;
export const MIN_DURATION_MINUTES = 30;
export const MAX_DURATION_MINUTES = 1440; // 24 hours
export const DURATION_STEP_MINUTES = 30;
